import { Form, Space } from 'antd';
import { Rule } from 'antd/lib/form';
import React from 'react';
import {
  TemplateNamesByValueByVariable,
  VariableModel,
} from '../../iasConfigSchema';

type Props = {
  variable: VariableModel;
  children: React.ReactNode;
  templateName?: string;
  usedUniqueValues?: TemplateNamesByValueByVariable;
};

const VariableFormItem: React.FC<Props> = ({
  variable,
  children,
  templateName,
  usedUniqueValues,
}) => {
  const rules: Rule[] = [
    {
      required: variable.required,
      message: 'This field is required.',
    },
  ];

  if (variable.type === 'string') {
    variable.regexes?.forEach((regex) => {
      if (variable.multiple) {
        rules.push({
          validator: async (_, value) => {
            if (!value || !Array.isArray(value)) return;

            const pattern = new RegExp(regex.pattern);
            const invalidValues = value.filter((v) => !pattern.test(v));

            if (invalidValues.length > 0) {
              throw new Error(
                regex.error_message ||
                  'Invalid format, expected format: ' + regex.pattern,
              );
            }
          },
        });
      } else {
        rules.push({
          pattern: new RegExp(regex.pattern),
          message:
            regex.error_message ||
            'Invalid format, expected format: ' + regex.pattern,
        });
      }
    });

    if (variable.unique) {
      rules.push({
        validator: async (_, value) => {
          if (value === undefined || value === null || value === '') return;

          const values: string[] = Array.isArray(value) ? value : [value];
          const usedValues = usedUniqueValues?.[variable.name] ?? {};
          const seenValues = new Set<string>();

          for (const v of values) {
            if (seenValues.has(v)) {
              throw new Error(`The value '${v}' is provided more than once.`);
            }
            seenValues.add(v);

            const templateNames = usedValues[v] ?? [];

            if (
              variable.unique === 'per-template' &&
              templateName !== undefined &&
              templateNames.includes(templateName)
            ) {
              throw new Error(`The value '${v}' is already used.`);
            }

            if (variable.unique === 'global' && templateNames.length > 0) {
              throw new Error(`The value '${v}' is already used.`);
            }
          }
        },
      });
    }
  }

  return (
    <Form.Item
      key={variable.name}
      name={['variables', variable.name]}
      label={<Space size={0}>{variable.display_name}</Space>}
      required={variable.required}
      rules={rules}
    >
      {children}
    </Form.Item>
  );
};

export default VariableFormItem;
